// ==================== TRAFFIC SERVICE ====================
// Real-time weather, hotspots, and speed cameras with Supabase + Open-Meteo

import { supabase, db } from './supabase';

// ==================== JAMAICA PARISH COORDINATES ====================
export const PARISH_COORDS = {
  'Kingston': { lat: 17.9714, lng: -76.7920 },
  'St. Andrew': { lat: 18.0179, lng: -76.7897 },
  'St. Catherine': { lat: 18.0419, lng: -77.0565 },
  'Clarendon': { lat: 17.9557, lng: -77.2396 },
  'Manchester': { lat: 18.0417, lng: -77.5086 },
  'St. Elizabeth': { lat: 18.0736, lng: -77.7117 },
  'Westmoreland': { lat: 18.2949, lng: -78.1591 },
  'Hanover': { lat: 18.4083, lng: -78.1310 },
  'St. James': { lat: 18.4762, lng: -77.9233 },
  'Trelawny': { lat: 18.3527, lng: -77.6075 },
  'St. Ann': { lat: 18.4298, lng: -77.2021 },
  'St. Mary': { lat: 18.3680, lng: -76.9030 },
  'Portland': { lat: 18.1761, lng: -76.4500 },
  'St. Thomas': { lat: 17.9310, lng: -76.3494 }
};

// Default to Kingston for weather
const DEFAULT_COORDS = PARISH_COORDS['Kingston'];

// ==================== WEATHER SERVICE (Open-Meteo - Free, No API Key) ====================
const WEATHER_CACHE = {
  data: null,
  timestamp: null,
  ttl: 10 * 60 * 1000 // 10 minute cache
};

// Weather code to description mapping
const WEATHER_CODES = {
  0: { desc: 'Clear sky', icon: 'sun' },
  1: { desc: 'Mainly clear', icon: 'sun' },
  2: { desc: 'Partly cloudy', icon: 'cloud-sun' },
  3: { desc: 'Overcast', icon: 'cloud' },
  45: { desc: 'Foggy', icon: 'cloud-fog' },
  48: { desc: 'Depositing rime fog', icon: 'cloud-fog' },
  51: { desc: 'Light drizzle', icon: 'cloud-drizzle' },
  53: { desc: 'Moderate drizzle', icon: 'cloud-drizzle' },
  55: { desc: 'Dense drizzle', icon: 'cloud-drizzle' },
  61: { desc: 'Slight rain', icon: 'cloud-rain' },
  63: { desc: 'Moderate rain', icon: 'cloud-rain' },
  65: { desc: 'Heavy rain', icon: 'cloud-rain' },
  80: { desc: 'Slight showers', icon: 'cloud-rain' },
  81: { desc: 'Moderate showers', icon: 'cloud-rain' },
  82: { desc: 'Violent showers', icon: 'cloud-rain' },
  95: { desc: 'Thunderstorm', icon: 'cloud-lightning' },
  96: { desc: 'Thunderstorm with hail', icon: 'cloud-lightning' },
  99: { desc: 'Severe thunderstorm', icon: 'cloud-lightning' }
};

/**
 * Get real weather for a Jamaica parish using Open-Meteo
 * @param {string} parish - Parish name (defaults to Kingston)
 * @returns {Promise<object>} Weather data
 */
export const getWeather = async (parish = 'Kingston') => {
  const coords = PARISH_COORDS[parish] || DEFAULT_COORDS;

  // Check cache first
  const cacheKey = `${coords.lat},${coords.lng}`;
  if (WEATHER_CACHE.data?.[cacheKey] &&
      Date.now() - WEATHER_CACHE.timestamp < WEATHER_CACHE.ttl) {
    return { data: WEATHER_CACHE.data[cacheKey], error: null, cached: true };
  }

  try {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}&longitude=${coords.lng}&current=temperature_2m,relative_humidity_2m,apparent_temperature,precipitation,weather_code,wind_speed_10m,wind_direction_10m&hourly=precipitation_probability&timezone=America/Jamaica&forecast_days=1`;

    const response = await fetch(url);
    if (!response.ok) throw new Error('Weather API error');

    const data = await response.json();
    const current = data.current;
    const weatherInfo = WEATHER_CODES[current.weather_code] || { desc: 'Unknown', icon: 'cloud' };

    // Get rain probability for next few hours
    const hourlyProb = data.hourly?.precipitation_probability || [];
    const currentHour = new Date().getHours();
    const rainProbability = hourlyProb[currentHour] || 0;

    const weatherData = {
      parish,
      temperature: Math.round(current.temperature_2m),
      feelsLike: Math.round(current.apparent_temperature),
      humidity: current.relative_humidity_2m,
      precipitation: current.precipitation,
      condition: weatherInfo.desc,
      icon: weatherInfo.icon,
      windSpeed: Math.round(current.wind_speed_10m),
      windDirection: current.wind_direction_10m,
      rainProbability,
      updatedAt: new Date().toISOString(),
      // Driving tips based on conditions
      drivingTips: generateDrivingTips(current.weather_code, rainProbability, current.wind_speed_10m)
    };

    // Update cache
    if (!WEATHER_CACHE.data) WEATHER_CACHE.data = {};
    WEATHER_CACHE.data[cacheKey] = weatherData;
    WEATHER_CACHE.timestamp = Date.now();

    return { data: weatherData, error: null };
  } catch (error) {
    console.error('Weather fetch error:', error);
    return {
      data: null,
      error: error.message,
      // Fallback data
      fallback: {
        parish,
        temperature: 30,
        feelsLike: 33,
        condition: 'Unable to fetch weather',
        icon: 'cloud',
        rainProbability: 0,
        drivingTips: ['Check weather before driving', 'Always drive safely']
      }
    };
  }
};

/**
 * Generate driving tips based on weather conditions
 */
const generateDrivingTips = (weatherCode, rainProb, windSpeed) => {
  const tips = [];

  // Rain/precipitation tips
  if (weatherCode >= 51 || rainProb > 50) {
    tips.push('Rain expected - reduce speed and increase following distance');
    tips.push('Use headlights in reduced visibility');
    tips.push('Watch for flooding on low-lying roads');
    tips.push('"Turn Around Don\'t Drown" - avoid flooded areas');
  }

  // Thunderstorm tips
  if (weatherCode >= 95) {
    tips.push('Severe weather - consider delaying travel');
    tips.push('Stay in vehicle if caught in lightning storm');
    tips.push('Pull over and wait out heavy storms');
  }

  // Wind tips
  if (windSpeed > 40) {
    tips.push('High winds - be cautious on bridges and open roads');
    tips.push('Watch for debris on roadways');
  }

  // Fog tips
  if (weatherCode === 45 || weatherCode === 48) {
    tips.push('Foggy conditions - use low beam headlights');
    tips.push('Reduce speed significantly in fog');
  }

  // Clear weather
  if (tips.length === 0) {
    tips.push('Good driving conditions');
    tips.push('Stay alert and hydrated in warm weather');
    tips.push('Watch for pedestrians and motorcyclists');
  }

  return tips;
};

// ==================== HOTSPOTS SERVICE ====================

// Expiration hours by type
const EXPIRATION_HOURS = {
  checkpoint: 6,      // Police checkpoints expire in 6 hours
  speed_trap: 4,      // Speed traps expire in 4 hours
  accident: 2,        // Accidents expire in 2 hours
  hazard: 8,          // Hazards expire in 8 hours
  traffic: 2,         // Traffic jams expire in 2 hours
  other: 4
};

/**
 * Get all active hotspots (not expired)
 */
export const getHotspots = async () => {
  if (!supabase) {
    // Fallback to localStorage if Supabase not configured
    const stored = localStorage.getItem('ja-hotspots');
    return { data: stored ? JSON.parse(stored) : [], error: null, source: 'local' };
  }

  try {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('hotspots')
      .select(`
        *,
        reporter:profiles!reported_by(full_name, reputation_score)
      `)
      .eq('is_active', true)
      .or(`expires_at.is.null,expires_at.gt.${now}`)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Add computed fields
    const enrichedData = data?.map(h => ({
      ...h,
      isExpired: h.expires_at && new Date(h.expires_at) < new Date(),
      isVerified: h.confirmations >= 3,
      timeAgo: getTimeAgo(h.created_at),
      expiresIn: h.expires_at ? getTimeUntil(h.expires_at) : null
    })) || [];

    return { data: enrichedData, error: null, source: 'supabase' };
  } catch (error) {
    console.error('Error fetching hotspots:', error);
    return { data: [], error: error.message, source: 'error' };
  }
};

/**
 * Create a new hotspot report
 */
export const createHotspot = async (hotspotData, userId = null) => {
  // Calculate expiration time based on type
  const expirationHours = EXPIRATION_HOURS[hotspotData.type] || 4;
  const expiresAt = new Date(Date.now() + expirationHours * 60 * 60 * 1000);

  if (!supabase) {
    // Fallback to localStorage
    const stored = localStorage.getItem('ja-hotspots');
    const hotspots = stored ? JSON.parse(stored) : [];
    const newHotspot = {
      id: Date.now(),
      ...hotspotData,
      confirmations: 1,
      is_active: true,
      expires_at: expiresAt.toISOString(),
      created_at: new Date().toISOString()
    };
    hotspots.unshift(newHotspot);
    localStorage.setItem('ja-hotspots', JSON.stringify(hotspots));
    return { data: newHotspot, error: null, source: 'local' };
  }

  try {
    const { data, error } = await supabase
      .from('hotspots')
      .insert({
        type: hotspotData.type,
        location: hotspotData.location || hotspotData.loc,
        parish: hotspotData.parish,
        description: hotspotData.description || hotspotData.desc,
        latitude: hotspotData.latitude,
        longitude: hotspotData.longitude,
        photo_url: hotspotData.photo_url,
        reported_by: userId,
        confirmations: 1,
        is_active: true,
        expires_at: expiresAt.toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    // Update reporter reputation if logged in
    if (userId) {
      await updateReporterReputation(userId, 'create_hotspot', 5);
    }

    return { data, error: null, source: 'supabase' };
  } catch (error) {
    console.error('Error creating hotspot:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Confirm/upvote a hotspot (extends expiration)
 */
export const confirmHotspot = async (hotspotId, userId = null) => {
  if (!supabase) {
    // Fallback to localStorage
    const stored = localStorage.getItem('ja-hotspots');
    const hotspots = stored ? JSON.parse(stored) : [];
    const updated = hotspots.map(h => {
      if (h.id === hotspotId) {
        const expirationHours = EXPIRATION_HOURS[h.type] || 4;
        return {
          ...h,
          confirmations: (h.confirmations || 1) + 1,
          last_confirmed: new Date().toISOString(),
          expires_at: new Date(Date.now() + expirationHours * 60 * 60 * 1000).toISOString()
        };
      }
      return h;
    });
    localStorage.setItem('ja-hotspots', JSON.stringify(updated));
    return { data: updated.find(h => h.id === hotspotId), error: null };
  }

  try {
    // Get current hotspot to check type for expiration extension
    const { data: current } = await supabase
      .from('hotspots')
      .select('type, confirmations')
      .eq('id', hotspotId)
      .single();

    const expirationHours = EXPIRATION_HOURS[current?.type] || 4;
    const newExpiration = new Date(Date.now() + expirationHours * 60 * 60 * 1000);

    const { data, error } = await supabase
      .from('hotspots')
      .update({
        confirmations: (current?.confirmations || 1) + 1,
        last_confirmed_at: new Date().toISOString(),
        expires_at: newExpiration.toISOString()
      })
      .eq('id', hotspotId)
      .select()
      .single();

    if (error) throw error;

    // Update confirmer reputation
    if (userId) {
      await updateReporterReputation(userId, 'confirm_hotspot', 1);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error confirming hotspot:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Downvote/flag a hotspot as incorrect
 */
export const flagHotspot = async (hotspotId, userId = null, reason = '') => {
  if (!supabase) {
    return { data: null, error: 'Flagging requires database connection' };
  }

  try {
    // Record the flag
    await supabase
      .from('hotspot_flags')
      .insert({
        hotspot_id: hotspotId,
        flagged_by: userId,
        reason
      });

    // Get current flag count
    const { count } = await supabase
      .from('hotspot_flags')
      .select('*', { count: 'exact', head: true })
      .eq('hotspot_id', hotspotId);

    // If 3+ flags, deactivate the hotspot
    if (count >= 3) {
      await supabase
        .from('hotspots')
        .update({ is_active: false })
        .eq('id', hotspotId);
    }

    return { data: { flagged: true, totalFlags: count }, error: null };
  } catch (error) {
    console.error('Error flagging hotspot:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Delete a hotspot (only owner or admin)
 */
export const deleteHotspot = async (hotspotId, userId = null) => {
  if (!supabase) {
    const stored = localStorage.getItem('ja-hotspots');
    const hotspots = stored ? JSON.parse(stored) : [];
    const filtered = hotspots.filter(h => h.id !== hotspotId);
    localStorage.setItem('ja-hotspots', JSON.stringify(filtered));
    return { error: null };
  }

  try {
    const { error } = await supabase
      .from('hotspots')
      .update({ is_active: false })
      .eq('id', hotspotId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting hotspot:', error);
    return { error: error.message };
  }
};

// ==================== SPEED CAMERAS SERVICE ====================

/**
 * Get all speed cameras (official + community)
 */
export const getSpeedCameras = async () => {
  if (!supabase) {
    const stored = localStorage.getItem('ja-user-cameras');
    const userCameras = stored ? JSON.parse(stored) : [];
    return { data: userCameras, error: null, source: 'local' };
  }

  try {
    const { data, error } = await supabase
      .from('speed_cameras')
      .select(`
        *,
        reporter:profiles!reported_by(full_name, reputation_score)
      `)
      .eq('is_active', true)
      .order('is_official', { ascending: false })
      .order('confirmations', { ascending: false });

    if (error) throw error;

    const enrichedData = data?.map(c => ({
      ...c,
      isVerified: c.is_official || c.confirmations >= 3,
      source: c.is_official ? 'official' : 'community'
    })) || [];

    return { data: enrichedData, error: null, source: 'supabase' };
  } catch (error) {
    console.error('Error fetching cameras:', error);
    return { data: [], error: error.message };
  }
};

/**
 * Create a new speed camera report
 */
export const createSpeedCamera = async (cameraData, userId = null) => {
  if (!supabase) {
    const stored = localStorage.getItem('ja-user-cameras');
    const cameras = stored ? JSON.parse(stored) : [];
    const newCamera = {
      id: Date.now(),
      ...cameraData,
      confirmations: 1,
      is_active: true,
      is_official: false,
      created_at: new Date().toISOString()
    };
    cameras.unshift(newCamera);
    localStorage.setItem('ja-user-cameras', JSON.stringify(cameras));
    return { data: newCamera, error: null, source: 'local' };
  }

  try {
    const { data, error } = await supabase
      .from('speed_cameras')
      .insert({
        location: cameraData.location || cameraData.loc,
        parish: cameraData.parish,
        direction: cameraData.direction,
        speed_limit: cameraData.speed_limit || cameraData.limit,
        latitude: cameraData.latitude,
        longitude: cameraData.longitude,
        photo_url: cameraData.photo_url,
        type: (cameraData.type || 'fixed').toLowerCase(),
        is_official: false,
        reported_by: userId,
        confirmations: 1,
        is_active: true
      })
      .select()
      .single();

    if (error) throw error;

    // Update reporter reputation
    if (userId) {
      await updateReporterReputation(userId, 'create_camera', 5);
    }

    return { data, error: null, source: 'supabase' };
  } catch (error) {
    console.error('Error creating camera:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Confirm/upvote a speed camera
 */
export const confirmSpeedCamera = async (cameraId, userId = null) => {
  if (!supabase) {
    const stored = localStorage.getItem('ja-user-cameras');
    const cameras = stored ? JSON.parse(stored) : [];
    const updated = cameras.map(c =>
      c.id === cameraId
        ? { ...c, confirmations: (c.confirmations || 1) + 1, last_confirmed: new Date().toISOString() }
        : c
    );
    localStorage.setItem('ja-user-cameras', JSON.stringify(updated));
    return { data: updated.find(c => c.id === cameraId), error: null };
  }

  try {
    const { data: current } = await supabase
      .from('speed_cameras')
      .select('confirmations')
      .eq('id', cameraId)
      .single();

    const { data, error } = await supabase
      .from('speed_cameras')
      .update({
        confirmations: (current?.confirmations || 1) + 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', cameraId)
      .select()
      .single();

    if (error) throw error;

    if (userId) {
      await updateReporterReputation(userId, 'confirm_camera', 1);
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error confirming camera:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Flag a speed camera as incorrect
 */
export const flagSpeedCamera = async (cameraId, userId = null, reason = '') => {
  if (!supabase) {
    return { data: null, error: 'Flagging requires database connection' };
  }

  try {
    await supabase
      .from('camera_flags')
      .insert({
        camera_id: cameraId,
        flagged_by: userId,
        reason
      });

    const { count } = await supabase
      .from('camera_flags')
      .select('*', { count: 'exact', head: true })
      .eq('camera_id', cameraId);

    if (count >= 3) {
      await supabase
        .from('speed_cameras')
        .update({ is_active: false })
        .eq('id', cameraId);
    }

    return { data: { flagged: true, totalFlags: count }, error: null };
  } catch (error) {
    console.error('Error flagging camera:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Delete a speed camera report
 */
export const deleteSpeedCamera = async (cameraId) => {
  if (!supabase) {
    const stored = localStorage.getItem('ja-user-cameras');
    const cameras = stored ? JSON.parse(stored) : [];
    const filtered = cameras.filter(c => c.id !== cameraId);
    localStorage.setItem('ja-user-cameras', JSON.stringify(filtered));
    return { error: null };
  }

  try {
    const { error } = await supabase
      .from('speed_cameras')
      .update({ is_active: false })
      .eq('id', cameraId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting camera:', error);
    return { error: error.message };
  }
};

// ==================== REPUTATION SYSTEM ====================

/**
 * Update reporter reputation score
 */
export const updateReporterReputation = async (userId, action, points) => {
  if (!supabase || !userId) return;

  try {
    // First get current reputation
    const { data: profile } = await supabase
      .from('profiles')
      .select('reputation_score')
      .eq('id', userId)
      .single();

    const currentScore = profile?.reputation_score || 0;

    await supabase
      .from('profiles')
      .update({
        reputation_score: currentScore + points,
        updated_at: new Date().toISOString()
      })
      .eq('id', userId);

  } catch (error) {
    console.error('Error updating reputation:', error);
  }
};

/**
 * Get reputation level based on score
 */
export const getReputationLevel = (score) => {
  if (score >= 100) return { level: 'Trusted', color: 'emerald', badge: '🏆' };
  if (score >= 50) return { level: 'Regular', color: 'blue', badge: '⭐' };
  if (score >= 20) return { level: 'Active', color: 'amber', badge: '✓' };
  return { level: 'New', color: 'gray', badge: '' };
};

// ==================== GEOLOCATION HELPERS ====================

/**
 * Get user's current location
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        reject(error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  });
};

/**
 * Calculate distance between two points (km)
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const toRad = (deg) => deg * (Math.PI / 180);

/**
 * Sort items by distance from user
 */
export const sortByDistance = (items, userLat, userLng) => {
  return items
    .map(item => ({
      ...item,
      distance: item.latitude && item.longitude
        ? calculateDistance(userLat, userLng, item.latitude, item.longitude)
        : null
    }))
    .sort((a, b) => {
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
};

/**
 * Format distance for display
 */
export const formatDistance = (km) => {
  if (km === null || km === undefined) return '';
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
};

// ==================== TIME HELPERS ====================

/**
 * Get time ago string
 */
export const getTimeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

/**
 * Get time until expiration
 */
export const getTimeUntil = (dateStr) => {
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m left`;
  const hours = Math.floor(mins / 60);
  return `${hours}h left`;
};

// ==================== PHOTO UPLOAD ====================

/**
 * Upload photo to Supabase storage
 */
export const uploadPhoto = async (file, bucket = 'hotspots') => {
  if (!supabase) {
    // Return base64 for localStorage mode
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve({ data: { url: e.target.result }, error: null });
      reader.readAsDataURL(file);
    });
  }

  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);

    return { data: { url: publicUrl, path: filePath }, error: null };
  } catch (error) {
    console.error('Error uploading photo:', error);
    return { data: null, error: error.message };
  }
};

export default {
  getWeather,
  getHotspots,
  createHotspot,
  confirmHotspot,
  flagHotspot,
  deleteHotspot,
  getSpeedCameras,
  createSpeedCamera,
  confirmSpeedCamera,
  flagSpeedCamera,
  deleteSpeedCamera,
  getCurrentLocation,
  calculateDistance,
  sortByDistance,
  formatDistance,
  getTimeAgo,
  getTimeUntil,
  uploadPhoto,
  getReputationLevel,
  PARISH_COORDS
};
